import { VIEWER_EXPRESS_CONTAINER_ID } from "../viewer/constants";

interface JsIntegrationsConfig {
  jsContent: Array<string> | undefined;
  onScriptLoad: Function | undefined;
}

const WIDGET_LIB_SCRIPT_ID = "bronte-aet-widget-lib";
const widgetAnchorId = (screenId: string): string => `aet-widget-anchor-${screenId}`;
const widgetMountXPath = (screenId: string): string => `//*[@id='${widgetAnchorId(screenId)}']`;

type WidgetWindow = Window & {
  renderFeedbackWidget?: (...args: unknown[]) => void;
  renderMCQWidget?: (...args: unknown[]) => void;
};

let widgetGlobalStylesRemoved = false;

const isWidgetStyleElement = (styleEl: HTMLStyleElement): boolean => {
  const css = styleEl.textContent ?? "";
  return css.includes("feed-back-main-container") || css.includes("--font-family-primary");
};

const isProtectedStyleElement = (styleEl: HTMLStyleElement): boolean =>
  styleEl.id === "viewer-express-container-styles" || styleEl.id === "viewer-express-theme-css";

const hasActiveWidgetForScreen = (screenId: string): boolean => {
  const anchor = document.getElementById(widgetAnchorId(screenId));
  return Boolean(anchor?.querySelector('[data-aet-widget-host="true"]'));
};

/** Remove prior reader widgets only — avoid global removeAllWidgets() (repaints whole page). */
const cleanupReaderWidgets = (): void => {
  const container = document.getElementById(VIEWER_EXPRESS_CONTAINER_ID);
  if (!container) {
    return;
  }

  container.querySelectorAll('[data-aet-widget-host="true"]').forEach((host) => host.remove());
  container.querySelectorAll('a[id^="aet-widget-anchor-"]').forEach((anchor) => anchor.remove());
};

/** widget.js global CSS collides with dev-shell (e.g. `.header` on drawer titles). Strip once on first load. */
const removeWidgetGlobalStylesOnce = (): void => {
  if (widgetGlobalStylesRemoved || !document.getElementById(VIEWER_EXPRESS_CONTAINER_ID)) {
    return;
  }

  for (const styleEl of Array.from(document.head.querySelectorAll("style"))) {
    if (!(styleEl instanceof HTMLStyleElement)) {
      continue;
    }
    if (isProtectedStyleElement(styleEl) || !isWidgetStyleElement(styleEl)) {
      continue;
    }
    styleEl.remove();
  }

  widgetGlobalStylesRemoved = true;
};

const appendInlineScript = (jsContentInitCommand: string): void => {
  const script = document.createElement("script");
  script.textContent = jsContentInitCommand;
  script.defer = true;
  document.head.append(script);
};

const appendInitScript = (screenId: string, jsContentInitCommand: string): void => {
  const viewerContainer = document.getElementById(VIEWER_EXPRESS_CONTAINER_ID);
  if (!viewerContainer) {
    appendInlineScript(jsContentInitCommand);
    return;
  }

  const anchorId = widgetAnchorId(screenId);

  if (
    jsContentInitCommand.includes("document.body.innerHTML") &&
    jsContentInitCommand.includes("data-id=")
  ) {
    appendInlineScript(`(function () {
      var screenId = ${JSON.stringify(screenId)};
      var anchorId = ${JSON.stringify(anchorId)};
      var anchor = document.createElement("div");
      anchor.dataset.id = screenId;
      anchor.id = anchorId;
      document.getElementById(${JSON.stringify(VIEWER_EXPRESS_CONTAINER_ID)}).appendChild(anchor);
    })();`);
    return;
  }

  const renderWidgetMatch = jsContentInitCommand.match(
    /\brender(?:Feedback|MCQ)Widget\s*\(\s*[^,]+\s*,\s*\{/,
  );
  if (renderWidgetMatch) {
    const withReaderXPath = jsContentInitCommand.replace(
      /(\brender(?:Feedback|MCQ)Widget\s*\(\s*[^,]+\s*,\s*)\{/,
      `$1{ xpath: ${JSON.stringify(widgetMountXPath(screenId))}, `,
    );
    appendInlineScript(withReaderXPath);
    return;
  }

  appendInlineScript(jsContentInitCommand);
};

const ensureWidgetLibrary = (jsContent?: string[]): void => {
  const widgetWindow = window as WidgetWindow;
  if (
    widgetWindow.renderFeedbackWidget ||
    widgetWindow.renderMCQWidget ||
    document.getElementById(WIDGET_LIB_SCRIPT_ID)
  ) {
    return;
  }
  if (!jsContent?.length) {
    return;
  }

  const script = document.createElement("script");
  script.textContent = jsContent.join(";");
  script.defer = true;
  script.id = WIDGET_LIB_SCRIPT_ID;
  document.head.append(script);
  removeWidgetGlobalStylesOnce();
};

const mountWidgetForScreen = (screenId: string, onScriptLoad?: Function): void => {
  if (!onScriptLoad) {
    return;
  }

  onScriptLoad((jsContentInitCommand: string) => {
    appendInitScript(screenId, jsContentInitCommand);
  });
};

const fetchDynamicJSScripts = async (
  screenId: string,
  jsIntegrations: Function,
  options?: { force?: boolean },
) => {
  if (!jsIntegrations || !document.getElementById(VIEWER_EXPRESS_CONTAINER_ID)) {
    return;
  }

  if (!options?.force && hasActiveWidgetForScreen(screenId)) {
    return;
  }

  cleanupReaderWidgets();

  const config = (await jsIntegrations(screenId)) as JsIntegrationsConfig | undefined;
  if (!config) {
    return;
  }

  ensureWidgetLibrary(config.jsContent);
  mountWidgetForScreen(screenId, config.onScriptLoad);
};

export const JsIntegrations = {
  fetchDynamicJSScripts,
};
